import {
  getLereDynamicPricingGroup,
  isEligibleForLereDynamicPricingAbTest,
} from 'checkout/lib/utils/lereDynamicPricing'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import useLereAuthSessionResets from 'hooks/useLereAuthSessionResets'
import {
  getEligibleRoomRateIds,
  resetEligibleRoomRatesCache,
} from 'lib/lere/eligibleRoomRates'
import { getEligibleSurchargeOfferIds } from 'lib/lere/eligibleSurchargeOffers'
import { ensureLereGroupAssignment } from 'lib/lere/lereGroupAssignment'
import {
  lereDecisionSessionState,
  type LereDynamicPricingAuthSession,
} from 'lib/lere/lereDynamicPricingLabSession'
import { hasLereLabDistinctId } from 'lib/lab/labDistinctId'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { selectLoggedIn } from 'selectors/accountSelectors'
import { isLereDynamicPricingExcluded } from 'lib/lere/lereDynamicPricingEligibility'

interface LereDynamicPricingGroupForRates {
  /** LERE group to pass to rates/calendar requests, when assigned. */
  lereGroup: string | undefined
  /** False while waiting for lab assignment; true when fetches may proceed. */
  isReady: boolean
}

interface LereDynamicPricingGroupForRatesOptions {
  shouldWait: boolean
  hasOpenedCalendar: boolean
  offerId: string
  checkIn?: string
  checkOut?: string
}

/** Request LERE assignment for an eligible calendar-open offer or selected-date room rate. */
export default function useLereDynamicPricingGroupForRates({
  shouldWait,
  hasOpenedCalendar,
  offerId,
  checkIn,
  checkOut,
}: LereDynamicPricingGroupForRatesOptions): LereDynamicPricingGroupForRates {
  const dispatch = useAppDispatch()
  const isLoggedIn = useAppSelector(selectLoggedIn)
  const lereGroup = useAppSelector(getLereDynamicPricingGroup)
  const hasDistinctId = useAppSelector(hasLereLabDistinctId)
  const offer = useAppSelector(state => state.offer.offers[offerId] ?? state.offer.offerSummaries[offerId])
  const isExcluded = useAppSelector(isLereDynamicPricingExcluded)
  const isLereDynamicPricingEnabled = isEligibleForLereDynamicPricingAbTest() && !isExcluded
  const shouldWaitForAssignment = shouldWait && isLereDynamicPricingEnabled
  const authSession: LereDynamicPricingAuthSession = isLoggedIn ? 'loggedIn' : 'loggedOut'
  const [eligibleSurchargeOfferIds, setEligibleSurchargeOfferIds] = useState<Set<string> | undefined>(undefined)
  const [eligibleRoomRateIds, setEligibleRoomRateIds] = useState<Set<string> | undefined>(undefined)
  const [eligibleRoomRatesDateRange, setEligibleRoomRatesDateRange] = useState<string | undefined>(undefined)
  const [lastCompletedAuthSession, setLastCompletedAuthSession] = useState<LereDynamicPricingAuthSession | undefined>(undefined)
  const lereGroupRef = useRef(lereGroup)
  const roomRateIds = useMemo(() => new Set(offer?.packages.flatMap(pkg => pkg.roomRate?.id ?? []) ?? []), [offer])
  const dateRangeKey = checkIn && checkOut ? `${checkIn}|${checkOut}` : undefined

  lereGroupRef.current = lereGroup

  const resetEligibleOffers = useCallback(() => {
    setLastCompletedAuthSession(undefined)
    resetEligibleRoomRatesCache()
    setEligibleRoomRateIds(undefined)
    setEligibleRoomRatesDateRange(undefined)
  }, [])

  useLereAuthSessionResets({
    onMemberIdChange: resetEligibleOffers,
    onLoggedInChange: resetEligibleOffers,
  })

  useEffect(() => {
    if (!isLereDynamicPricingEnabled || lereGroup || !hasOpenedCalendar) {
      return
    }

    let cancelled = false

    getEligibleSurchargeOfferIds()
      .then(offerIds => {
        if (!cancelled) {
          setEligibleSurchargeOfferIds(offerIds)
        }
      })
      .catch(() => {
        if (!cancelled) {
          setEligibleSurchargeOfferIds(new Set())
        }
      })

    return () => {
      cancelled = true
    }
  }, [hasOpenedCalendar, isLereDynamicPricingEnabled, lereGroup])

  useEffect(() => {
    if (!isLereDynamicPricingEnabled || lereGroup || !checkIn || !checkOut || !dateRangeKey) {
      return
    }

    let cancelled = false

    async function loadEligibleRoomRateIds() {
      try {
        const roomRateIds = await getEligibleRoomRateIds(checkIn as string, checkOut as string)

        if (!cancelled) {
          setEligibleRoomRateIds(roomRateIds)
          setEligibleRoomRatesDateRange(dateRangeKey)
        }
      } catch {
        if (!cancelled) {
          setEligibleRoomRateIds(new Set())
          setEligibleRoomRatesDateRange(dateRangeKey)
        }
      }
    }

    loadEligibleRoomRateIds()

    return () => {
      cancelled = true
    }
  }, [checkIn, checkOut, dateRangeKey, isLereDynamicPricingEnabled, lereGroup])

  const isEligibleOfferReady = !hasOpenedCalendar || eligibleSurchargeOfferIds !== undefined
  const isEligibleRoomRatesReady = !dateRangeKey || eligibleRoomRatesDateRange === dateRangeKey
  const isCalendarEntryEligible = hasOpenedCalendar && !!eligibleSurchargeOfferIds?.has(offerId)
  const isSelectedDatesEntryEligible = !!dateRangeKey && !!eligibleRoomRateIds && Array.from(roomRateIds).some(roomRateId => eligibleRoomRateIds.has(roomRateId))
  const isEntryEligible = isCalendarEntryEligible || isSelectedDatesEntryEligible
  const isEntryEligibilityReady = isEntryEligible || (isEligibleOfferReady && isEligibleRoomRatesReady)

  const hasCompletedDecision = lereDecisionSessionState[authSession].completed
  const shouldRequestLereDynamicPricingGroup = shouldWaitForAssignment
    && isEntryEligibilityReady
    && isEntryEligible
    && hasDistinctId

  useEffect(() => {
    if (!shouldRequestLereDynamicPricingGroup) {
      return
    }

    const requestedAuthSession = authSession

    ensureLereGroupAssignment({
      dispatch,
      authSession: requestedAuthSession,
      getLereGroup: () => lereGroupRef.current,
      hasDistinctId,
    }).then(() => {
      setLastCompletedAuthSession(requestedAuthSession)
    }).catch(() => {})
  }, [authSession, dispatch, hasDistinctId, shouldRequestLereDynamicPricingGroup])

  if (!shouldWaitForAssignment) {
    return { lereGroup: undefined, isReady: true }
  }

  if (lereGroup) {
    return { lereGroup, isReady: true }
  }

  if (!isEntryEligibilityReady) {
    return { lereGroup: undefined, isReady: false }
  }

  if (!isEntryEligible || !hasDistinctId) {
    return { lereGroup: undefined, isReady: true }
  }

  return {
    lereGroup,
    isReady: hasCompletedDecision || lastCompletedAuthSession === authSession,
  }
}
